import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { DATE_FORMATS, formatDate, showError } from 'utils';
import { tableStyles } from '../../../css/tableStyles';
import { getPageLimit, updatePageLimit } from 'utils';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import CustomLink from 'components/TableLink';
import { deleteRecipe, fetchPaginatedRecipes } from 'services';
import DeleteActionForTable from 'components/DeleteActionForTableData';

const filters: { [p: string]: any; page: number } = {
  page: 0,
};

const Recipes = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [recipes, setRecipes] = useState<any>({});
  const { page, ...rest } = filters;
  const fetchPaginatedRecipesFn = async () => {
    setLoading(true);
    try {
      const response = await fetchPaginatedRecipes({
        page,
        limit: getPageLimit(),
        filter: { ...rest },
      });
      setRecipes(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaginatedRecipesFn();
  }, []);

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    fetchPaginatedRecipesFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const deleteRecipeFn = async (id: string) => {
    try {
      await deleteRecipe(id);
      await fetchPaginatedRecipesFn();
    } catch (e) {
      showError(e);
    }
  };

  const columns = useMemo(() => [
    {
      name: 'Id',
      selector: 'objectId',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/recipes/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: 'name',
      sortable: true,
    },
    {
      name: 'Description',
      selector: 'description',
      sortable: true,
    },
    {
      name: 'Slug',
      selector: 'slug',
      sortable: true,
      width: '120px',
    },
    {
      name: 'CreatedAt',
      selector: 'createdAt',
      sortable: true,
      width: '180px',
      format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'ml-3'}>
          <DeleteActionForTable
            onClick={() => deleteRecipeFn(row.objectId)}
          />
        </div>
      ),
    },
  ], []);

  const getHeader = () => {
    return (
      <div className={styles['header']}>
        <div className={styles['heading']}>Recipes</div>
        <div className={styles['buttons-container']}>
          <ActionButton
            text={<>Recipe <span>+</span></>}
            active={true}
            isLoading={false}
            onClick={() => { props.history.push('/recipes/create') }}
            customClass={styles['add-new-recipe']}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className={styles['main-container']}>
        {getHeader()}
        <DataTable
          keyField="objectId"
          columns={columns}
          noHeader={true}
          data={recipes.items}
          highlightOnHover={true}
          customStyles={tableStyles}
          pagination={true}
          paginationServer={true}
          paginationPerPage={getPageLimit()}
          paginationDefaultPage={(filters.page || 0) + 1}
          paginationTotalRows={recipes.total}
          paginationRowsPerPageOptions={[50, 100, 200]}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default Recipes;
